import { DISEASE_CHARACTERISTICS, findDiseaseByName } from '@/data/disease-types'
import { HERBICIDE_PRODUCTS } from '@/data/herbicide-products'
import { PEST_CHARACTERISTICS, findPestByName } from '@/data/pest-types'
import {
  getProductByKey,
  type ProductKey,
  type ProductRecommendation,
} from '@/data/guide-product-recommendations'
import { WEED_CHARACTERISTICS, findWeedByName } from '@/data/weed-types'
import type { DiagnosisIdentification, TreatmentRecommendation } from '@/lib/types/diagnosis'
import { NUTRIENT_TREATMENTS, STRESS_TREATMENTS } from '@/lib/diagnosis-constants'

/**
 * Curation workflow for diagnosis product picks:
 * 1. Start from the KB treatmentOptions.chemical active ingredient for the condition.
 * 2. Find a homeowner-appropriate ASIN with that active, plus the necessary tool/surfactant companion.
 * 3. Run `npm run paapi:lookup <ASIN>` and verify the live title, price, and imageId.
 * 4. Add or update the catalog entry in `data/guide-product-recommendations.ts`.
 * 5. Add the ASIN to `data/amazon-product-enrichment.ts` so `npm run paapi:refresh`
 *    covers price/image drift.
 */

export type Season = 'spring' | 'summer' | 'fall' | 'winter'
export type ConditionType = DiagnosisIdentification['type']
export type ConditionPickRole = 'curative' | 'preventive' | 'recovery' | 'tool'
export type ConditionProductKey = ProductKey

export interface ConditionPick {
  productKey: ConditionProductKey
  role: ConditionPickRole
  minSeverity?: 'moderate' | 'severe'
  safeGrassTypes?: string[]
  unsafeGrassTypes?: string[]
  seasons?: Season[]
  note?: string
}

export interface ConditionPickDefinition {
  conditionType: ConditionType
  picks: ConditionPick[]
}

export interface ResolvedConditionPick extends ConditionPick {
  product: ProductRecommendation
}

export type ConditionPicksResult =
  | {
      source: 'curated'
      conditionSlug: string
      conditionType: ConditionType
      picks: ResolvedConditionPick[]
    }
  | {
      source: 'fallback'
      conditionSlug?: string
      conditionType?: ConditionType
      picks: []
      fallbackReason: 'unmatched' | 'too-few-curated-picks'
    }

export interface ConditionMatch {
  conditionSlug: string
  conditionType: ConditionType
}

const ANY_SEASON: Season[] = ['spring', 'summer', 'fall', 'winter']
const WARM_ACTIVE: Season[] = ['spring', 'summer', 'fall']
const COOL_ACTIVE: Season[] = ['spring', 'fall']
const SUMMER_STRESS: Season[] = ['summer']

const COMMON_DISEASE_RECOVERY: ConditionPick[] = [
  {
    productKey: 'fungicide-diseaseex',
    role: 'curative',
    minSeverity: 'moderate',
    seasons: WARM_ACTIVE,
    note: 'Azoxystrobin broad-spectrum fungicide for active lawn disease pressure.',
  },
  {
    productKey: 'tool-spreader-edgeguard-mini',
    role: 'tool',
    seasons: ANY_SEASON,
    note: 'Use a calibrated spreader so granular fungicide lands evenly.',
  },
  {
    productKey: 'soil-sea-kelp',
    role: 'recovery',
    seasons: WARM_ACTIVE,
    note: 'Kelp helps stressed turf recover after disease pressure is under control.',
  },
]

const COOL_DISEASE_RECOVERY: ConditionPick[] = [
  {
    productKey: 'fungicide-diseaseex',
    role: 'curative',
    minSeverity: 'moderate',
    seasons: COOL_ACTIVE,
    note: 'Use during cool, damp disease windows when symptoms are spreading.',
  },
  {
    productKey: 'tool-spreader-edgeguard-mini',
    role: 'tool',
    seasons: ANY_SEASON,
    note: 'Even coverage matters more than extra product.',
  },
  {
    productKey: 'fertilizer-simple-1648',
    role: 'recovery',
    seasons: WARM_ACTIVE,
    note: 'Light balanced feeding helps thin turf fill back in once disease slows.',
  },
]

export const CONDITION_PICKS: Record<string, ConditionPickDefinition> = {
  // Diseases
  'dollar-spot': {
    conditionType: 'disease',
    picks: [
      {
        productKey: 'fungicide-diseaseex',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: WARM_ACTIVE,
        note: 'Dollar spot responds to azoxystrobin when cultural fixes are not enough.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'A light nitrogen push helps turf outgrow dollar spot scarring.',
      },
      {
        productKey: 'tool-hose-end-sprayer',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use for foliar recovery products and spot treatments.',
      },
    ],
  },
  'brown-patch': {
    conditionType: 'disease',
    picks: COMMON_DISEASE_RECOVERY,
  },
  rust: {
    conditionType: 'disease',
    picks: [
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Rust usually improves when the lawn is growing steadily again.',
      },
      {
        productKey: 'fungicide-diseaseex',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: WARM_ACTIVE,
        note: 'Reserve fungicide for spreading or high-confidence rust outbreaks.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Even spreader coverage prevents striped recovery.',
      },
    ],
  },
  'red-thread': {
    conditionType: 'disease',
    picks: COOL_DISEASE_RECOVERY,
  },
  'pythium-blight': {
    conditionType: 'disease',
    picks: COMMON_DISEASE_RECOVERY,
  },
  'gray-snow-mold': {
    conditionType: 'disease',
    picks: COOL_DISEASE_RECOVERY,
  },
  'pink-snow-mold': {
    conditionType: 'disease',
    picks: COOL_DISEASE_RECOVERY,
  },
  'fairy-ring': {
    conditionType: 'disease',
    picks: [
      {
        productKey: 'summer-wetting-agent',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'A wetting agent helps water move through hydrophobic fairy-ring soil.',
      },
      {
        productKey: 'tool-hose-end-sprayer',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Apply wetting agents evenly around the ring and just beyond it.',
      },
      {
        productKey: 'soil-humic-acid',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Humic acid supports soil recovery after the water-repellency issue is corrected.',
      },
    ],
  },
  'leaf-spot': {
    conditionType: 'disease',
    picks: COMMON_DISEASE_RECOVERY,
  },
  'summer-patch': {
    conditionType: 'disease',
    picks: COMMON_DISEASE_RECOVERY,
  },
  'large-patch': {
    conditionType: 'disease',
    picks: COMMON_DISEASE_RECOVERY,
  },

  // Weeds
  crabgrass: {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-quinclorac',
        role: 'curative',
        seasons: ['spring', 'summer'],
        note: 'Quinclorac is the post-emergent crabgrass pick when it is safe for the grass.',
      },
      {
        productKey: 'pre-emergent-barricade',
        role: 'preventive',
        seasons: ['spring', 'fall'],
        note: 'Prodiamine prevents the next crabgrass wave before it germinates.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Pull isolated crowns before they produce seed without risking the surrounding turf.',
      },
      {
        productKey: 'pre-emergent-dimension',
        role: 'preventive',
        seasons: ['spring', 'fall'],
        note: 'Dithiopyr is a second prevention option when its label matches the verified lawn.',
      },
    ],
  },
  dandelions: {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-trimec',
        role: 'curative',
        seasons: COOL_ACTIVE,
        note: 'A three-way broadleaf herbicide clears active dandelions on tolerant lawns.',
      },
      {
        productKey: 'post-emergent-atrazine-southern',
        role: 'curative',
        safeGrassTypes: ['St. Augustinegrass', 'Centipede', 'Zoysiagrass'],
        seasons: WARM_ACTIVE,
        note: 'Southern option for St. Augustine and centipede lawns that cannot take Trimec.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'A stand-up puller helps remove the full dandelion taproot.',
      },
    ],
  },
  'white-clover': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-tzone',
        role: 'curative',
        safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass'],
        seasons: COOL_ACTIVE,
        note: 'T-Zone is the stronger option for clover and other stubborn broadleaf weeds.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use for scattered rooted clumps where a whole-lawn spray is unnecessary.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Clover often points to thin, low-nitrogen turf.',
      },
    ],
  },
  'common-lespedeza': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-trimec',
        role: 'curative',
        seasons: ['spring', 'summer'],
        note: 'Three-way broadleaf herbicides are a proven post-emergent option for young common lespedeza. Check the label for your grass before applying.',
      },
      {
        productKey: 'pre-emergent-dimension',
        role: 'preventive',
        seasons: ['spring'],
        note: 'A spring dithiopyr application can prevent the next common lespedeza crop before it germinates.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Pull isolated plants with the taproot before stems harden and seed develops.',
      },
      {
        productKey: 'tool-hose-end-sprayer',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use a calibrated sprayer for even spot treatment, following the herbicide label for dilution and coverage.',
      },
    ],
  },
  'foxtail-setaria': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-quinclorac',
        role: 'curative',
        seasons: ['spring', 'summer'],
        note: 'Quinclorac is an option for young foxtail only when the verified host grass is label-compatible.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Pull isolated clumps before the bottlebrush seed head matures.',
      },
      {
        productKey: 'pre-emergent-barricade',
        role: 'preventive',
        seasons: ['spring'],
        note: 'Use a labeled prevention pass before late-spring foxtail germination.',
      },
    ],
  },
  goosegrass: {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-tenacity',
        role: 'curative',
        seasons: ['spring', 'summer'],
        note: 'Mesotrione can target young goosegrass only on a verified compatible lawn.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Dig isolated flattened crowns before the finger-like seed spikes develop.',
      },
      {
        productKey: 'pre-emergent-barricade',
        role: 'preventive',
        seasons: ['spring'],
        note: 'Prevention is most useful before the later goosegrass germination window.',
      },
    ],
  },
  'broadleaf-plantain': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-speedzone',
        role: 'curative',
        seasons: COOL_ACTIVE,
        note: 'SpeedZone gives fast broadleaf knockdown when plantain is actively growing.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Plantain rosettes and their short crowns lift cleanly from moist soil.',
      },
      {
        productKey: 'soil-humic-acid',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Plantain often follows compacted, tired soil. Pair control with soil recovery.',
      },
    ],
  },
  'nutsedge-yellow': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-sedgehammer',
        role: 'curative',
        seasons: ['spring', 'summer'],
        note: 'Halosulfuron targets sedges that regular broadleaf sprays ignore.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use only for very young isolated sedges; repeated pulling of mature patches can spread pressure.',
      },
      {
        productKey: 'summer-wetting-agent',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Fix wet, poorly draining areas so nutsedge pressure drops.',
      },
    ],
  },
  'creeping-charlie-ground-ivy': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-tzone',
        role: 'curative',
        safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass'],
        seasons: COOL_ACTIVE,
        note: 'Triclopyr-based broadleaf control is reserved for a verified compatible cool-season lawn.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Lift small patches and remove every rooted runner node.',
      },
      {
        productKey: 'fertilizer-pgf-complete',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Thicker compatible turf helps close the damp, thin gaps ground ivy occupies.',
      },
    ],
  },
  'annual-bluegrass-poa-annua': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'pre-emergent-barricade',
        role: 'preventive',
        seasons: ['fall'],
        note: 'Fall prodiamine is the prevention window for Poa annua.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Remove isolated light-green tufts before their low seed heads mature.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Granular prevention only works when spread evenly.',
      },
    ],
  },
  'spotted-spurge': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-speedzone',
        role: 'curative',
        seasons: WARM_ACTIVE,
        note: 'Spurge is easiest to control while young and actively growing.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Wear gloves and pull the shallow-rooted mat before it sets seed.',
      },
      {
        productKey: 'pre-emergent-barricade',
        role: 'preventive',
        seasons: ['spring'],
        note: 'Prevention matters because spurge spreads low and fast from seed.',
      },
    ],
  },
  henbit: {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-trimec',
        role: 'curative',
        seasons: COOL_ACTIVE,
        note: 'Use a labeled broadleaf treatment only after the host turf is verified compatible.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Young winter-annual plants pull readily from moist soil before flowering.',
      },
      {
        productKey: 'pre-emergent-barricade',
        role: 'preventive',
        seasons: ['fall'],
        note: 'A labeled fall prevention pass targets the next winter-annual germination cycle.',
      },
    ],
  },
  'wild-violet': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-tzone',
        role: 'curative',
        safeGrassTypes: ['Kentucky Bluegrass', 'Tall Fescue', 'Fine Fescue', 'Perennial Ryegrass'],
        seasons: COOL_ACTIVE,
        note: 'Triclopyr plus broadleaf actives is the stronger route for waxy violet leaves.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use on small colonies and remove the rhizomes, then monitor for regrowth.',
      },
      {
        productKey: 'fertilizer-pgf-complete',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Thicker turf helps close the gaps wild violet uses.',
      },
    ],
  },
  'chickweed-common': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-speedzone',
        role: 'curative',
        seasons: COOL_ACTIVE,
        note: 'Chickweed is a cool-season broadleaf, so treat while it is actively growing.',
      },
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Rake and pull shallow-rooted mats before they flower.',
      },
      {
        productKey: 'fertilizer-pgf-complete',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Dense turf is the best follow-up after chickweed control.',
      },
    ],
  },

  // Pests
  'white-grubs-japanese-beetle': {
    conditionType: 'pest',
    picks: [
      {
        productKey: 'grub-control-grubex',
        role: 'preventive',
        seasons: ['spring', 'summer'],
        note: 'Chlorantraniliprole is the prevention pick before grubs feed hard.',
      },
      {
        productKey: 'pest-sevin',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: ['summer', 'fall'],
        note: 'Use broad-spectrum insect control only when active feeding is confirmed.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Granular grub products need uniform coverage and watering-in.',
      },
    ],
  },
  'white-grubs-european-chafer': {
    conditionType: 'pest',
    picks: [
      {
        productKey: 'grub-control-grubex',
        role: 'preventive',
        seasons: ['spring', 'summer'],
        note: 'Apply before larvae start feeding near the surface.',
      },
      {
        productKey: 'pest-sevin',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: ['spring', 'fall'],
        note: 'Confirm active grubs before using a curative insecticide.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use an even spread pattern, then water in.',
      },
    ],
  },
  'chinch-bugs': {
    conditionType: 'pest',
    picks: [
      {
        productKey: 'pest-sevin',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: SUMMER_STRESS,
        note: 'Use after a flotation test confirms active chinch bugs.',
      },
      {
        productKey: 'summer-hydretain',
        role: 'recovery',
        seasons: SUMMER_STRESS,
        note: 'Moisture support helps turf recover from hot-edge chinch damage.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use for granular insect-control passes.',
      },
    ],
  },
  'sod-webworms': {
    conditionType: 'pest',
    picks: [
      {
        productKey: 'pest-sevin',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: SUMMER_STRESS,
        note: 'Treat only when larvae are present in thatch.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Granules need to reach the thatch layer evenly.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Light feeding helps chewed turf rebuild leaf tissue.',
      },
    ],
  },
  armyworms: {
    conditionType: 'pest',
    picks: [
      {
        productKey: 'pest-sevin',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: ['summer', 'fall'],
        note: 'Armyworms move fast. Treat confirmed active feeding promptly.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Bermuda and other warm-season lawns can rebound after feeding and water.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use for even granular insect-control coverage.',
      },
    ],
  },
  billbugs: {
    conditionType: 'pest',
    picks: [
      {
        productKey: 'grub-control-grubex',
        role: 'preventive',
        seasons: ['spring', 'summer'],
        note: 'Preventive grub products can help where billbugs recur.',
      },
      {
        productKey: 'pest-sevin',
        role: 'curative',
        minSeverity: 'moderate',
        seasons: SUMMER_STRESS,
        note: 'Use only after stem-pull signs confirm active damage.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Recovery feeding helps fill billbug-thinned areas.',
      },
    ],
  },

  // Nutrients
  nitrogen: {
    conditionType: 'nutrient_deficiency',
    picks: [
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Balanced liquid feed for a controlled nitrogen correction.',
      },
      {
        productKey: 'fertilizer-milorganite',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Slow-release nitrogen lowers burn risk on stressed lawns.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use for even granular feeding.',
      },
    ],
  },
  iron: {
    conditionType: 'nutrient_deficiency',
    picks: [
      {
        productKey: 'iron-petratools',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Chelated iron improves color without pushing heavy top growth.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Use when iron chlorosis is paired with broader nutrient hunger.',
      },
      {
        productKey: 'tool-hose-end-sprayer',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Liquid iron needs even foliar coverage.',
      },
    ],
  },
  potassium: {
    conditionType: 'nutrient_deficiency',
    picks: [
      {
        productKey: 'fertilizer-pgf-complete',
        role: 'recovery',
        seasons: ['spring', 'fall'],
        note: 'Balanced 16-4-8 helps correct low potassium without overdoing nitrogen.',
      },
      {
        productKey: 'fertilizer-simple-1648',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Liquid 16-4-8 is a smaller correction pass for active growth.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Granular potassium corrections need calibrated coverage.',
      },
    ],
  },
  phosphorus: {
    conditionType: 'nutrient_deficiency',
    picks: [
      {
        productKey: 'starter-scotts',
        role: 'recovery',
        seasons: ['spring', 'fall'],
        note: 'Use phosphorus only when a soil test or establishment need justifies it.',
      },
      {
        productKey: 'tool-spreader-edgeguard-mini',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Keep phosphorus applications precise and off hard surfaces.',
      },
    ],
  },

  // Environmental stress
  drought: {
    conditionType: 'environmental',
    picks: [
      {
        productKey: 'summer-hydretain',
        role: 'recovery',
        seasons: SUMMER_STRESS,
        note: 'Hydretain supports stressed root zones between deeper watering cycles.',
      },
      {
        productKey: 'summer-wetting-agent',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Wetting agents help water move into dry, hydrophobic soil.',
      },
      {
        productKey: 'smart-soil-sensor',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'A moisture sensor helps avoid guessing during drought stress.',
      },
    ],
  },
  heat: {
    conditionType: 'environmental',
    picks: [
      {
        productKey: 'summer-hydretain',
        role: 'recovery',
        seasons: SUMMER_STRESS,
        note: 'Heat stress usually starts with water stress at the root zone.',
      },
      {
        productKey: 'iron-petratools',
        role: 'recovery',
        seasons: SUMMER_STRESS,
        note: 'Iron gives color without a heavy nitrogen push in hot weather.',
      },
      {
        productKey: 'smart-soil-sensor',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Measure moisture before adding more fertilizer or water.',
      },
    ],
  },
  'dog-urine': {
    conditionType: 'environmental',
    picks: [
      {
        productKey: 'summer-wetting-agent',
        role: 'recovery',
        seasons: WARM_ACTIVE,
        note: 'Flush and dilute salts first, then improve water movement through the spot.',
      },
      {
        productKey: 'tool-hose-end-sprayer',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Use for spot-flushing and light recovery products.',
      },
      {
        productKey: 'seed-cool-pennington',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Reseed cool-season dead spots once crowns are gone.',
      },
    ],
  },
  quackgrass: {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Works on small patches only, and only if you lift out every white rhizome piece; any fragment left behind regrows into a new plant.',
      },
      {
        productKey: 'seed-cool-pennington',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Removing or spot-treating quackgrass leaves bare soil, so reseed it. Late summer to early fall is the strongest window for cool-season seed.',
      },
      {
        productKey: 'fertilizer-pgf-complete',
        role: 'preventive',
        seasons: COOL_ACTIVE,
        note: 'Feeding will not kill quackgrass, but a dense well-fed stand mowed tall shades the rhizome tips that surface at the edge of a patch.',
      },
    ],
  },

  orchardgrass: {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'tool-fiskars-weed-puller',
        role: 'tool',
        seasons: ANY_SEASON,
        note: 'Orchardgrass is a bunchgrass with no rhizomes, so lifting a small clump with its crown and root mass genuinely ends that plant. Large established clumps still want a spade.',
      },
      {
        productKey: 'seed-cool-pennington',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Every clump you remove leaves a divot. Refill it with certified cool-season seed, which is also how you stop reintroducing orchardgrass through bargain mixes.',
      },
      {
        productKey: 'starter-scotts',
        role: 'recovery',
        seasons: COOL_ACTIVE,
        note: 'Starter food helps the reseeded patches root before surrounding turf or the next weed closes over them.',
      },
    ],
  },

  'creeping-bentgrass': {
    conditionType: 'weed',
    picks: [
      {
        productKey: 'post-emergent-tenacity',
        role: 'curative',
        seasons: ['summer', 'fall'],
        note: 'The one selective option, and only on a verified Kentucky bluegrass, perennial ryegrass, or tall fescue lawn. Plan on three or four applications 2 to 3 weeks apart; a single pass will not do it.',
      },
      {
        productKey: 'surfactant-non-ionic',
        role: 'tool',
        seasons: ['summer', 'fall'],
        note: 'The mesotrione label requires a non-ionic surfactant for post-emergent applications. Without it, coverage on fine bentgrass blades is the limiting factor.',
      },
      {
        productKey: 'seed-cool-pennington',
        role: 'recovery',
        seasons: ['fall'],
        note: 'Stripped or bleached patches have to be refilled the same season or the stolons come back. Check the seeding tolerance on the herbicide label before overseeding a treated area.',
      },
    ],
  },

}

const SEVERITY_RANK: Record<DiagnosisIdentification['severity'], number> = {
  mild: 0,
  moderate: 1,
  severe: 2,
}

const HERBICIDE_PRODUCT_KEYS: Partial<Record<ConditionProductKey, keyof typeof HERBICIDE_PRODUCTS>> = {
  'pre-emergent-barricade': 'Barricade 4FL',
  'pre-emergent-dimension': 'Dimension 2EW',
  'post-emergent-trimec': 'Trimec Classic',
  'post-emergent-speedzone': 'SpeedZone',
  'post-emergent-tenacity': 'Tenacity',
  'post-emergent-quinclorac': 'Quinclorac 75DF',
  'post-emergent-sedgehammer': 'SedgeHammer',
}

const CONDITION_ALIASES: Record<string, string> = {
  'dollar spot': 'dollar-spot',
  'brown patch disease': 'brown-patch',
  'brown patch': 'brown-patch',
  'large patch': 'large-patch',
  'snow mold': 'gray-snow-mold',
  'gray snow mold': 'gray-snow-mold',
  'pink snow mold': 'pink-snow-mold',
  'leaf spot': 'leaf-spot',
  'summer patch': 'summer-patch',
  'pythium': 'pythium-blight',
  'pythium blight': 'pythium-blight',
  dandelion: 'dandelions',
  dandelions: 'dandelions',
  clover: 'white-clover',
  'white clover': 'white-clover',
  'common lespedeza': 'common-lespedeza',
  'common lespedeza japanese clover': 'common-lespedeza',
  'japanese clover': 'common-lespedeza',
  'annual lespedeza': 'common-lespedeza',
  nutsedge: 'nutsedge-yellow',
  'yellow nutsedge': 'nutsedge-yellow',
  'poa annua': 'annual-bluegrass-poa-annua',
  'annual bluegrass': 'annual-bluegrass-poa-annua',
  plantain: 'broadleaf-plantain',
  'wild violet': 'wild-violet',
  violet: 'wild-violet',
  chickweed: 'chickweed-common',
  grubs: 'white-grubs-japanese-beetle',
  grub: 'white-grubs-japanese-beetle',
  'white grubs': 'white-grubs-japanese-beetle',
  armyworm: 'armyworms',
  armyworms: 'armyworms',
  'chinch bug': 'chinch-bugs',
  'chinch bugs': 'chinch-bugs',
  drought: 'drought',
  dry: 'drought',
  'heat stress': 'heat',
  heat: 'heat',
  'dog urine': 'dog-urine',
  urine: 'dog-urine',
  chlorosis: 'iron',
  'iron chlorosis': 'iron',
}

export function slugifyConditionName(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\([^)]*\)/g, (match) => ` ${match.slice(1, -1)} `)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function resolveConditionMatch(
  problem: Pick<DiagnosisIdentification, 'type' | 'name' | 'matchedSlug'> | undefined | null
): ConditionMatch | null {
  if (!problem?.name && !problem?.matchedSlug) return null

  if (problem.matchedSlug && CONDITION_PICKS[problem.matchedSlug]) {
    return {
      conditionSlug: problem.matchedSlug,
      conditionType: CONDITION_PICKS[problem.matchedSlug].conditionType,
    }
  }

  const name = problem.name ?? ''
  const normalizedName = normalizeText(name)
  const alias = CONDITION_ALIASES[normalizedName]
  if (alias && CONDITION_PICKS[alias]) {
    return { conditionSlug: alias, conditionType: CONDITION_PICKS[alias].conditionType }
  }

  if (problem.type === 'disease') {
    const disease = findDiseaseByName(name) ?? findByLooseName(DISEASE_CHARACTERISTICS, name)
    if (disease?.id && CONDITION_PICKS[disease.id]) {
      return { conditionSlug: disease.id, conditionType: 'disease' }
    }
  }

  if (problem.type === 'pest') {
    const pest = findPestByName(name) ?? findByLooseName(PEST_CHARACTERISTICS, name)
    if (pest?.id && CONDITION_PICKS[pest.id]) {
      return { conditionSlug: pest.id, conditionType: 'pest' }
    }
  }

  if (problem.type === 'weed') {
    const weed = findWeedByName(name) ?? findByLooseName(WEED_CHARACTERISTICS, name)
    if (weed?.name) {
      const slug = slugifyConditionName(weed.name)
      if (CONDITION_PICKS[slug]) return { conditionSlug: slug, conditionType: 'weed' }
    }
  }

  if (problem.type === 'nutrient_deficiency') {
    const nutrient = findTreatmentKey(normalizedName, NUTRIENT_TREATMENTS)
    if (nutrient && CONDITION_PICKS[nutrient]) {
      return { conditionSlug: nutrient, conditionType: 'nutrient_deficiency' }
    }
  }

  if (problem.type === 'environmental') {
    const stress = findEnvironmentalKey(normalizedName)
    if (stress && CONDITION_PICKS[stress]) {
      return { conditionSlug: stress, conditionType: 'environmental' }
    }
  }

  return null
}

const CONDITION_TREATMENT_FALLBACKS: Record<
  string,
  Omit<TreatmentRecommendation, 'problemName' | 'products'>
> = {
  'common-lespedeza': {
    culturalPractices: [
      'Hand-pull isolated plants with the taproot before they set seed.',
      'For larger patches, spot-treat with a post-emergent product whose label lists common lespedeza and your turfgrass.',
      'Relieve compaction and thicken thin turf so new lespedeza seedlings have less room to establish.',
    ],
    organicAlternatives: [
      'Pull or dig plants while the soil is moist and before the stems become woody.',
      'Mow at the correct height and repair thin areas after control to improve turf competition.',
    ],
    bestTreatmentWindow: 'Late spring to early summer while plants are young and actively growing',
  },
}

const CONDITION_TYPE_TREATMENT_FALLBACKS: Record<
  ConditionType,
  Omit<TreatmentRecommendation, 'problemName' | 'products'>
> = {
  disease: {
    culturalPractices: [
      'Start by correcting prolonged leaf wetness: water early, avoid evening irrigation, and improve airflow through the affected area.',
      'Bag visibly affected clippings while symptoms are active and clean mower blades before moving to healthy turf.',
      'If the problem continues to spread, use a lawn fungicide whose label lists the diagnosed disease and follow its timing and reapplication directions.',
    ],
    organicAlternatives: [
      'Reduce irrigation frequency while keeping the root zone adequately moist.',
      'Avoid heavy nitrogen until active symptoms slow, then support recovery with light feeding if the lawn needs it.',
    ],
    bestTreatmentWindow: 'Begin cultural corrections now; use a labeled fungicide while symptoms are active if the affected area is spreading',
  },
  pest: {
    culturalPractices: [
      'Confirm active insects in the damaged area with a tug, soap-flush, or soil inspection before applying an insecticide.',
      'Reduce excess thatch and correct drought stress so the turf can recover after feeding damage.',
      'When active pests are confirmed, use a lawn insect-control product whose label lists the diagnosed pest and follow its watering-in directions.',
    ],
    organicAlternatives: [
      'Remove small, localized infestations and damaged material by hand where practical.',
      'Support recovery with correct mowing and deep, infrequent watering after active feeding stops.',
    ],
    bestTreatmentWindow: 'Treat while the diagnosed pest is active and present in the damaged area',
  },
  weed: {
    culturalPractices: [
      'Hand-pull isolated plants, removing the crown or root system before seed develops.',
      'For larger patches, spot-treat with a post-emergent product whose label lists this weed and your turfgrass.',
      'Repair thin turf after control so new weeds have less open soil to colonize.',
    ],
    organicAlternatives: [
      'Pull or dig plants while the soil is moist and dispose of seed heads.',
      'Mow at the correct height and thicken bare areas after removal.',
    ],
    bestTreatmentWindow: 'Treat young, actively growing weeds before they flower or set seed',
  },
  nutrient_deficiency: {
    culturalPractices: [
      'Confirm the suspected deficiency with a soil test before making a large fertilizer correction.',
      'Use the smallest labeled nutrient application that addresses the confirmed need, then water it in as directed.',
      'Recheck color and growth before repeating an application so the lawn is not overfed.',
    ],
    organicAlternatives: [
      'Use compost or a slow-release organic fertilizer only when the soil result supports it.',
      'Correct pH and irrigation problems that can keep existing nutrients unavailable to roots.',
    ],
    bestTreatmentWindow: 'Correct confirmed deficiencies while the grass is actively growing',
  },
  environmental: {
    culturalPractices: [
      'Correct the source of stress first, such as watering, mowing height, drainage, traffic, or recent product injury.',
      'Protect the affected area from additional stress while the crowns and roots recover.',
      'Repair dead or thin areas during the next suitable growing window after the stress source is controlled.',
    ],
    organicAlternatives: [
      'Use water, compost, and reduced traffic as appropriate to the diagnosed stress instead of adding another pesticide.',
      'Monitor new growth for two weeks before adding fertilizer to already stressed turf.',
    ],
    bestTreatmentWindow: 'Correct the stress source now and repair turf during its active growing season',
  },
}

const CONDITION_TYPE_COMPANION_PICKS: Record<ConditionType, ConditionPick[]> = {
  disease: [
    { productKey: 'tool-spreader-edgeguard-mini', role: 'tool', note: 'Even coverage matters when a granular treatment or recovery product is appropriate.' },
    { productKey: 'soil-sea-kelp', role: 'recovery', note: 'Kelp supports root recovery after disease pressure and cultural conditions are corrected.' },
    { productKey: 'smart-soil-sensor', role: 'tool', note: 'Track root-zone moisture so the lawn is not kept wet enough to prolong disease.' },
  ],
  pest: [
    { productKey: 'tool-spreader-edgeguard-mini', role: 'tool', note: 'Apply labeled granular pest control evenly when active insects are confirmed.' },
    { productKey: 'summer-hydretain', role: 'recovery', note: 'Moisture support helps damaged turf recover after active feeding is controlled.' },
    { productKey: 'smart-soil-sensor', role: 'tool', note: 'Monitor soil moisture so drought stress does not compound feeding damage.' },
  ],
  weed: [
    { productKey: 'tool-fiskars-weed-puller', role: 'tool', note: 'Remove isolated plants and their crowns or roots without spraying the surrounding lawn.' },
    { productKey: 'tool-hose-end-sprayer', role: 'tool', note: 'Use for even, label-directed spot applications when a liquid treatment is appropriate.' },
    { productKey: 'tool-spreader-edgeguard-mini', role: 'tool', note: 'Apply granular prevention evenly and keep product off beds and hard surfaces.' },
  ],
  nutrient_deficiency: [
    { productKey: 'fertilizer-simple-1648', role: 'recovery', note: 'A measured balanced feeding option after the suspected nutrient need is confirmed.' },
    { productKey: 'tool-spreader-edgeguard-mini', role: 'tool', note: 'Calibrated coverage helps prevent streaks and overapplication.' },
    { productKey: 'soil-humic-acid', role: 'recovery', note: 'Supports soil structure and nutrient availability while the underlying deficiency is corrected.' },
  ],
  environmental: [
    { productKey: 'smart-soil-sensor', role: 'tool', note: 'Measure root-zone moisture before adding water or fertilizer to stressed turf.' },
    { productKey: 'tool-hose-end-sprayer', role: 'tool', note: 'Useful for controlled spot watering and label-directed liquid recovery products.' },
    { productKey: 'summer-wetting-agent', role: 'recovery', note: 'Helps water move through dry or water-repellent soil after the stress source is identified.' },
  ],
}

/**
 * Last-resort backfill so the three-card shelf is always full, even if a
 * condition-specific or type-companion productKey ever goes stale in the
 * catalog (or a future diagnosis type ships without enough companions). These
 * are generic, always-relevant essentials with no severity, season, or grass
 * gate, so they always pass the filters. Appended AFTER condition and companion
 * picks, so they only fill genuine gaps and never displace a relevant match.
 * Keep more than three here so the guarantee survives one entry going missing.
 */
const UNIVERSAL_BACKFILL_PICKS: ConditionPick[] = [
  {
    productKey: 'smart-soil-sensor',
    role: 'tool',
    note: 'Check root-zone moisture before watering or feeding so treatment is based on real conditions, not guesswork.',
  },
  {
    productKey: 'tool-spreader-edgeguard-mini',
    role: 'tool',
    note: 'Apply any granular product evenly and keep it off beds and hard surfaces.',
  },
  {
    productKey: 'fertilizer-simple-1648',
    role: 'recovery',
    note: 'A measured balanced feed supports recovery once the underlying problem is addressed.',
  },
  {
    productKey: 'soil-humic-acid',
    role: 'recovery',
    note: 'Supports soil structure and nutrient availability while the lawn recovers.',
  },
]

/**
 * Supplies deterministic treatment guidance for supported diagnosis conditions
 * that are intentionally outside the narrower Weed Identifier taxonomy.
 * This also repairs older saved results whose model finding was actionable but
 * whose original enrichment produced an empty treatment array.
 */
export function getConditionTreatmentFallback(
  problem: DiagnosisIdentification | undefined | null,
): TreatmentRecommendation | null {
  if (!problem) return null
  const match = resolveConditionMatch(problem)
  const fallback = (match ? CONDITION_TREATMENT_FALLBACKS[match.conditionSlug] : null)
    ?? CONDITION_TYPE_TREATMENT_FALLBACKS[problem.type]

  return {
    problemName: problem.name,
    products: [],
    culturalPractices: [...fallback.culturalPractices],
    organicAlternatives: [...fallback.organicAlternatives],
    bestTreatmentWindow: fallback.bestTreatmentWindow,
  }
}

/**
 * Result-shelf contract for a confident, supported diagnosis. Condition and
 * season matches lead; same-condition off-season options and safe companion
 * roles fill any gaps. This intentionally leaves getConditionPicks' narrower
 * 2-pick fallback behavior intact for older consumers while result pages get a
 * deterministic three-card shelf.
 */
export function getCompleteConditionPicks(
  problem: DiagnosisIdentification | undefined | null,
  grassType?: string | null,
  season?: Season | string | null,
  options: {
    hostGrassVerified?: boolean
    allowUnverifiedGrassProducts?: boolean
  } = {},
): ConditionPicksResult {
  if (!problem) return { source: 'fallback', picks: [], fallbackReason: 'unmatched' }

  const match = resolveConditionMatch(problem)
  const normalizedSeason = normalizeSeason(season)
  const definitionPicks = match ? CONDITION_PICKS[match.conditionSlug].picks : []
  // Fill order is honesty-ranked: in-season condition matches first, then
  // season-agnostic companions and universal essentials, and only then the
  // condition's OFF-season picks as a last resort. This keeps an out-of-window
  // product (e.g. a spring pre-emergent in summer, whose note assumes spring)
  // from taking a slot ahead of a season-neutral, still-relevant filler.
  const candidates = [
    ...definitionPicks.filter((pick) => passesSeason(pick, normalizedSeason)),
    ...CONDITION_TYPE_COMPANION_PICKS[problem.type],
    ...UNIVERSAL_BACKFILL_PICKS,
    ...definitionPicks,
  ]
  const seen = new Set<ConditionProductKey>()
  const picks: ResolvedConditionPick[] = []

  for (const pick of candidates) {
    if (picks.length >= 3 || seen.has(pick.productKey)) continue
    if (!passesSeverity(pick, problem.severity)) continue
    if (!passesGrassSafety(
      pick,
      grassType,
      options.hostGrassVerified === true,
      options.allowUnverifiedGrassProducts === true,
    )) continue
    const product = getProductByKey(pick.productKey)
    if (!product) continue
    seen.add(pick.productKey)
    picks.push({ ...pick, product })
  }

  if (picks.length < 3) {
    return {
      source: 'fallback',
      conditionSlug: match?.conditionSlug,
      conditionType: match?.conditionType ?? problem.type,
      picks: [],
      fallbackReason: 'too-few-curated-picks',
    }
  }

  return {
    source: 'curated',
    conditionSlug: match?.conditionSlug ?? (slugifyConditionName(problem.name) || `diagnosis-${problem.type}`),
    conditionType: match?.conditionType ?? problem.type,
    picks: picks.slice(0, 3),
  }
}

export function getConditionPicks(
  problem: DiagnosisIdentification | undefined | null,
  grassType?: string | null,
  season?: Season | string | null,
  options: {
    hostGrassVerified?: boolean
    minimumPicks?: number
    allowUnverifiedGrassProducts?: boolean
  } = {},
): ConditionPicksResult {
  const match = resolveConditionMatch(problem)
  if (!match || !problem) {
    return { source: 'fallback', picks: [], fallbackReason: 'unmatched' }
  }

  const definition = CONDITION_PICKS[match.conditionSlug]
  const normalizedSeason = normalizeSeason(season)
  const hydrated = definition.picks
    .filter((pick) => passesSeverity(pick, problem.severity))
    .filter((pick) => passesSeason(pick, normalizedSeason))
    .filter((pick) => passesGrassSafety(
      pick,
      grassType,
      options.hostGrassVerified === true,
      options.allowUnverifiedGrassProducts === true,
    ))
    .map((pick): ResolvedConditionPick | null => {
      const product = getProductByKey(pick.productKey)
      return product ? { ...pick, product } : null
    })
    .filter((pick): pick is ResolvedConditionPick => Boolean(pick))
    .slice(0, 3)

  if (hydrated.length < (options.minimumPicks ?? 2)) {
    return {
      source: 'fallback',
      conditionSlug: match.conditionSlug,
      conditionType: match.conditionType,
      picks: [],
      fallbackReason: 'too-few-curated-picks',
    }
  }

  return {
    source: 'curated',
    conditionSlug: match.conditionSlug,
    conditionType: match.conditionType,
    picks: hydrated,
  }
}

function normalizeText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function normalizeGrass(value: string): string {
  return normalizeText(value).replace(/\bgrass\b/g, '').replace(/\s+/g, ' ').trim()
}

function normalizeSeason(season: Season | string | null | undefined): Season | null {
  if (season === 'spring' || season === 'summer' || season === 'fall' || season === 'winter') {
    return season
  }
  return null
}

function findByLooseName<T extends { name: string }>(
  records: Record<string, T>,
  name: string
): T | undefined {
  const normalized = normalizeText(name)
  return Object.values(records).find((record) => {
    const recordName = normalizeText(record.name)
    return recordName.includes(normalized) || normalized.includes(recordName)
  })
}

function findTreatmentKey(
  normalizedName: string,
  treatments: Record<string, unknown>
): string | null {
  const aliases: Record<string, string> = {
    'yellowing between veins': 'iron',
    'iron chlorosis': 'iron',
    chlorosis: 'iron',
    'n deficiency': 'nitrogen',
    'low nitrogen': 'nitrogen',
    yellowing: 'nitrogen',
    'k deficiency': 'potassium',
    'low potassium': 'potassium',
    'p deficiency': 'phosphorus',
    'low phosphorus': 'phosphorus',
    'mg deficiency': 'magnesium',
    'low magnesium': 'magnesium',
  }
  const alias = Object.entries(aliases).find(([key]) => normalizedName.includes(key))
  if (alias) return alias[1]
  return Object.keys(treatments).find((key) => normalizedName.includes(key)) ?? null
}

function findEnvironmentalKey(normalizedName: string): string | null {
  const aliases: Record<string, string> = {
    dry: 'drought',
    drought: 'drought',
    'water stress': 'drought',
    'heat stress': 'heat',
    heat: 'heat',
    'dog urine': 'dog-urine',
    urine: 'dog-urine',
    'pet urine': 'dog-urine',
    'chemical burn': 'dog-urine',
    'fertilizer burn': 'dog-urine',
  }
  const alias = Object.entries(aliases).find(([key]) => normalizedName.includes(key))
  if (alias) return alias[1]
  return Object.keys(STRESS_TREATMENTS).find((key) => normalizedName.includes(key)) ?? null
}

function passesSeverity(pick: ConditionPick, severity: DiagnosisIdentification['severity']): boolean {
  if (!pick.minSeverity) return true
  return SEVERITY_RANK[severity] >= SEVERITY_RANK[pick.minSeverity]
}

function passesSeason(pick: ConditionPick, season: Season | null): boolean {
  if (!season || !pick.seasons || pick.seasons.length === 0) return true
  return pick.seasons.includes(season)
}

function passesGrassSafety(
  pick: ConditionPick,
  grassType: string | null | undefined,
  hostGrassVerified: boolean,
  allowUnverifiedGrassProducts = false,
): boolean {
  const herbicideSafe = getHerbicideSafeGrassTypes(pick.productKey)
  const hasGrassRestriction = Boolean(herbicideSafe || pick.safeGrassTypes || pick.unsafeGrassTypes)

  // Most analysis surfaces fail closed until the host grass is verified. The
  // dedicated Weed shelf can opt into showing the curated product as a
  // recommendation before that step; its UI then carries a product-level label
  // compatibility instruction instead of replacing the shelf with a blocker.
  if (hasGrassRestriction && (!grassType || !hostGrassVerified)) {
    return allowUnverifiedGrassProducts
  }
  if (!grassType) return true

  const normalizedGrass = normalizeGrass(grassType)
  const unsafe = pick.unsafeGrassTypes ?? []
  if (unsafe.some((grass) => normalizeGrass(grass) === normalizedGrass)) return false

  const safe = herbicideSafe ?? pick.safeGrassTypes
  if (!safe || safe.length === 0) return true
  return safe.some((grass) => normalizeGrass(grass) === normalizedGrass)
}

export function conditionPickNeedsGrassCheck(pick: ConditionPick): boolean {
  return Boolean(
    getHerbicideSafeGrassTypes(pick.productKey) ||
    pick.safeGrassTypes ||
    pick.unsafeGrassTypes,
  )
}

function getHerbicideSafeGrassTypes(productKey: ConditionProductKey): string[] | undefined {
  const herbicideKey = HERBICIDE_PRODUCT_KEYS[productKey]
  if (!herbicideKey) return undefined
  return HERBICIDE_PRODUCTS[herbicideKey]?.safeGrassTypes
}
